import { useEffect, useState } from 'react';
import BrandMark from '../common/BrandMark';
import AgentAvatar from '../common/AgentAvatar';
import styles from './HowItWorks.module.css';

const STEPS = [
  {
    n: '01',
    title: 'Tell your agents who you sell to.',
    body: 'Titles, industries, size, territory: set your seller profile once and every hunt inherits it.',
  },
  {
    n: '02',
    title: 'Ask for what you need in plain English.',
    body: '“Find me 25 VPs of Ops at Southeast logistics companies.” The Hunter searches, the Verifier checks.',
  },
  {
    n: '03',
    title: 'Get contacts that are actually real.',
    body: 'Every lead is deliverability-checked, employment-confirmed, and ICP-vetted before it counts.',
  },
];

const PREVIEW_TITLES = ['seller profile', 'the ask', 'the list'];

// Keep the auto-advance interval and the .progressFill animation duration in sync.
const ADVANCE_MS = 6000;

const PROFILE_ROWS = [
  { label: 'target titles', chips: ['VP of Operations', 'Director of Logistics'] },
  { label: 'industry',      chips: ['Logistics & Freight'] },
  { label: 'company size',  chips: ['50–500 employees'] },
  { label: 'territory',     chips: ['Southeast US'] },
];

// Same survivors as the LeadDifference gauntlet - the story carries through.
const LIST_ROWS = [
  { name: 'Sarah Chen',     role: 'VP Ops · Meridian Freight',          email: 's.chen@meridianfreight.com' },
  { name: 'Dana Whitfield', role: 'VP Ops · Crestline 3PL',             email: 'dwhitfield@crestline3pl.com' },
  { name: 'Priya Nair',     role: 'Head of Ops · Southway Carriers',    email: 'priya.nair@southwaycarriers.com' },
  { name: 'Elena Vasquez',  role: 'Ops Director · Bluegrass Logistics', email: 'evasquez@bluegrasslog.com' },
];

export default function HowItWorks() {
  const [step, setStep] = useState(0);

  // Auto-advance a beat after each change (manual clicks reset the timer for free).
  // Reduced motion: no auto-advance - the steps still work as plain tabs.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), ADVANCE_MS);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <section id="how-it-works" className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <BrandMark size={40} className={styles.headMark} />
          <span className={styles.label}>How it works</span>
          <h2 className={styles.heading}>Three steps. One chat.</h2>
        </div>

        <div className={styles.steps}>
          {STEPS.map((s, i) => (
            <button
              type="button"
              key={s.n}
              className={`${styles.step} ${i === step ? styles.stepActive : ''}`}
              onClick={() => setStep(i)}
              aria-current={i === step ? 'step' : undefined}
            >
              <span className={styles.num}>{s.n}</span>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepBody}>{s.body}</p>
              {i === step && (
                <span className={styles.progress} aria-hidden="true">
                  <span className={styles.progressFill} />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Shared preview: one panel, driven by the selected step */}
        <div className={styles.preview} aria-hidden="true">
          <div className={styles.previewBar}>
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewTitle}>{PREVIEW_TITLES[step]}</span>
          </div>

          {/* key remounts the body on step change so entrance animations replay */}
          <div key={step} className={styles.previewBody}>
            {step === 0 && (
              <div>
                {PROFILE_ROWS.map((r, i) => (
                  <div key={r.label} className={`${styles.pRow} ${styles.rise}`} style={{ animationDelay: `${i * 130}ms` }}>
                    <span className={styles.pLabel}>{r.label}</span>
                    <span className={styles.pChips}>
                      {r.chips.map((c) => <span key={c} className={styles.pChip}>{c}</span>)}
                    </span>
                  </div>
                ))}
                <p className={`${styles.pFoot} ${styles.rise}`} style={{ animationDelay: '560ms' }}>
                  Set once. Every hunt inherits it.
                </p>
              </div>
            )}

            {step === 1 && (
              <div>
                <div className={`${styles.youBubble} ${styles.rise}`}>
                  Find me 25 VPs of Ops at Southeast logistics companies.
                </div>
                <div className={`${styles.agentLine} ${styles.rise}`} style={{ animationDelay: '500ms' }}>
                  <span className={`${styles.aTag} ${styles.aHunter}`}>
                    <AgentAvatar agent="hunter" size={20} /> Hunter
                  </span>
                  <span className={styles.aText}>Searching… <strong>25 companies</strong> match your profile.</span>
                </div>
                <div className={`${styles.agentLine} ${styles.rise}`} style={{ animationDelay: '1000ms' }}>
                  <span className={`${styles.aTag} ${styles.aVerifier}`}>
                    <AgentAvatar agent="verifier" size={20} /> Verifier
                  </span>
                  <span className={styles.aText}>Checking deliverability + employment…</span>
                </div>
                <p className={`${styles.pFoot} ${styles.rise}`} style={{ animationDelay: '1500ms' }}>
                  No filters, no query builder. Just say it.
                </p>
              </div>
            )}

            {step === 2 && (
              <div>
                {LIST_ROWS.map((r, i) => (
                  <div key={r.name} className={`${styles.lRow} ${styles.rise}`} style={{ animationDelay: `${i * 140}ms` }}>
                    <span className={styles.lCheck}>✓</span>
                    <span className={styles.lWho}>
                      <span className={styles.lName}>{r.name}</span>
                      <span className={styles.lRole}>{r.role}</span>
                    </span>
                    <span className={styles.lEmail}>{r.email}</span>
                  </div>
                ))}
                <p className={`${styles.pFoot} ${styles.rise}`} style={{ animationDelay: '700ms' }}>
                  18 of 25 verified · 7 filtered out · zero bounces on your sender reputation
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
